import classNames from 'classnames';

import React, { HTMLAttributes } from 'react';

import { Icon } from '../icon';
import { Typography } from '../typography';

import styles from './tab-bar-item.scss';

type ItemContentProps = {
  icon: string;
  variant?: string;
  active?: boolean;
  counter?: number;
  children?: React.ReactNode;
  dataTestId?: string;
};

const COMPONENT_ID = 'modulor-tab-bar-item';

const ItemContent: React.FC<ItemContentProps> = ({
  icon,
  variant,
  active,
  counter,
  children,
  dataTestId = COMPONENT_ID,
}) => {
  const itemIcon = active ? icon.replace('outlined', 'filled') : icon;

  const iconClasses = classNames(styles['tab-bar-icon'], {
    [styles.active]: active,
  });
  const typographyClasses = classNames(styles.name, {
    [styles.active]: active,
  });

  return (
    <>
      <Icon
        name={itemIcon}
        count={counter}
        dataTestId={`${dataTestId}-icon`}
        className={iconClasses}
      />
      <Typography className={typographyClasses} dataTestId={dataTestId} variant={variant}>
        {children}
      </Typography>
    </>
  );
};

type Props = HTMLAttributes<HTMLDivElement> & {
  icon: string;
  variant?: string;
  active?: boolean;
  counter?: number;
  href?: string;
  horizontal?: boolean;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler;
  children?: React.ReactNode;
  dataTestId?: string;
};

export const TabBarItem: React.FC<Props> = ({
  icon,
  variant = 'caption-2-medium',
  active,
  counter,
  href,
  horizontal = false,
  style,
  onClick,
  children,
  dataTestId = COMPONENT_ID,
  className,
}) => {
  const classes = classNames(className, {
    [styles.item]: true,
    [styles.horizontal]: horizontal,
  });

  return href ? (
    <a
      className={classes}
      href={href}
      onClick={onClick}
      data-id={dataTestId}
      data-testid={dataTestId}
      data-component-name={COMPONENT_ID}
      style={{ ...style }}>
      <ItemContent
        icon={icon}
        variant={variant}
        active={active}
        counter={counter}
        dataTestId={`${dataTestId}-content`}>
        {children}
      </ItemContent>
    </a>
  ) : (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      data-testid={dataTestId}
      data-id={dataTestId}
      data-component-name={COMPONENT_ID}
      style={{ ...style }}>
      <ItemContent
        icon={icon}
        variant={variant}
        active={active}
        counter={counter}
        dataTestId={`${dataTestId}-content`}>
        {children}
      </ItemContent>
    </button>
  );
};
